/**
 * Accessible Accordion Custom Element
 * Follows WCAG accessibility guidelines for expandable content
 *
 * USAGE:
 * ======
 *
 * Basic usage:
 * <accordion-component>
 *   <accordion-item>
 *     <accordion-header>First Item</accordion-header>
 *     <accordion-content>
 *       <p>Content for first item</p>
 *     </accordion-content>
 *   </accordion-item>
 *   <accordion-item>
 *     <accordion-header>Second Item</accordion-header>
 *     <accordion-content>
 *       <div class="rich-text">Rich text content</div>
 *       <div class="liquid-content">{{ product.description }}</div>
 *     </accordion-content>
 *   </accordion-item>
 * </accordion-component>
 *
 * Advanced usage with attributes:
 * <accordion-component allow-multiple>
 *   <accordion-item open>
 *     <accordion-header>Open by Default</accordion-header>
 *     <accordion-content>This item starts open</accordion-content>
 *   </accordion-item>
 * </accordion-component>
 *
 * ATTRIBUTES:
 * ===========
 *
 * accordion-component:
 *   - allow-multiple: Allow multiple items to be open simultaneously
 *
 * accordion-item:
 *   - open: Item starts in open state
 *
 * EVENTS:
 * =======
 *
 * accordion-component dispatches:
 *   - accordion:open - When an item opens
 *   - accordion:close - When an item closes
 *
 * ACCESSIBILITY:
 * ==============
 *
 * - Full ARIA support (aria-expanded, aria-controls, etc.)
 * - Keyboard navigation:
 *   - Enter/Space: Toggle item
 *   - Arrow Up/Down: Navigate between headers
 *   - Home: Focus first header
 *   - End: Focus last header
 * - Screen reader friendly
 * - Respects prefers-reduced-motion
 *
 * CSS CLASSES:
 * ============
 *
 * The component automatically applies these classes:
 *   - .accordion-header (on accordion-header elements)
 *   - .accordion-content (on accordion-content elements)
 *   - .is-open (on open accordion-content elements)
 *
 * LIQUID INTEGRATION:
 * ===================
 *
 * Perfect for Shopify sections with rich text + liquid content:
 *
 * {% schema %}
 * {
 *   "blocks": [
 *     {
 *       "type": "accordion_item",
 *       "settings": [
 *         {
 *           "type": "text",
 *           "id": "title",
 *           "label": "Title"
 *         },
 *         {
 *           "type": "richtext",
 *           "id": "content",
 *           "label": "Content"
 *         },
 *         {
 *           "type": "liquid",
 *           "id": "liquid_content",
 *           "label": "Liquid Content"
 *         },
 *         {
 *           "type": "checkbox",
 *           "id": "open_by_default",
 *           "label": "Open by default"
 *         }
 *       ]
 *     }
 *   ]
 * }
 * {% endschema %}
 *
 * <accordion-component>
 *   {% for block in section.blocks %}
 *     <accordion-item {% if block.settings.open_by_default %}open{% endif %}>
 *       <accordion-header>{{ block.settings.title }}</accordion-header>
 *       <accordion-content>
 *         {% if block.settings.content != blank %}
 *           <div class="rich-text">{{ block.settings.content }}</div>
 *         {% endif %}
 *         {% if block.settings.liquid_content != blank %}
 *           <div class="liquid-content">{{ block.settings.liquid_content }}</div>
 *         {% endif %}
 *       </accordion-content>
 *     </accordion-item>
 *   {% endfor %}
 * </accordion-component>
 */
// Custom Element: AccordionItem
class AccordionItem extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.header = null;
    this.content = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Find header and content elements
    this.header = this.querySelector("accordion-header");
    this.content = this.querySelector("accordion-content");

    if (!this.header || !this.content) {
      console.warn("AccordionItem requires accordion-header and accordion-content children");
      return;
    }

    this.setupElements();
    this.setupAria();
    this.bindEvents();

    // Check if should be open by default
    if (this.hasAttribute("open")) {
      this.open(false); // false = no animation on init
    }
  }

  setupElements() {
    // Add CSS classes for styling
    this.header.classList.add("accordion-header");
    this.content.classList.add("accordion-content");

    // Make header interactive
    this.header.setAttribute("tabindex", "0");
    this.header.setAttribute("role", "button");

    // Add chevron icon if not present
    if (!this.header.querySelector(".accordion-icon")) {
      const icon = document.createElement("div");
      icon.className = "accordion-icon";
      icon.innerHTML = `
        <svg role="presentation" focusable="false" width="8" height="6" viewBox="0 0 8 6">
          <path d="m1 1.5 3 3 3-3" fill="none" stroke="currentColor" stroke-width="1.5"></path>
        </svg>
      `;
      this.header.appendChild(icon);
    }
  }

  setupAria() {
    // Generate unique IDs
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 7);
    const itemId = `accordion-${timestamp}-${random}`;
    const headerId = `${itemId}-header`;
    const contentId = `${itemId}-content`;

    // Set up ARIA attributes
    this.header.setAttribute("id", headerId);
    this.header.setAttribute("aria-controls", contentId);
    this.header.setAttribute("aria-expanded", "false");

    this.content.setAttribute("id", contentId);
    this.content.setAttribute("aria-labelledby", headerId);
    this.content.setAttribute("role", "region");
  }

  bindEvents() {
    // Click event
    this.header.addEventListener("click", (e) => {
      e.preventDefault();
      this.toggle();
    });

    // Keyboard event
    this.header.addEventListener("keydown", (e) => {
      switch (e.key) {
        case "Enter":
        case " ":
          e.preventDefault();
          this.toggle();
          break;
        case "ArrowDown":
          e.preventDefault();
          this.focusNext();
          break;
        case "ArrowUp":
          e.preventDefault();
          this.focusPrevious();
          break;
        case "Home":
          e.preventDefault();
          this.focusFirst();
          break;
        case "End":
          e.preventDefault();
          this.focusLast();
          break;
      }
    });
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      // Check if parent allows multiple open items
      const parent = this.closest("accordion-component");
      if (parent && !parent.hasAttribute("allow-multiple")) {
        // Close all other items
        parent.closeAllItems();
      }
      this.open();
    }
  }

  open(animate = true) {
    if (this.isOpen) return;

    // Update ARIA
    this.header.setAttribute("aria-expanded", "true");

    if (animate) {
      // Temporarily show content to measure its natural height
      this.content.style.display = "block";
      this.content.style.visibility = "hidden";
      this.content.style.height = "auto";
      this.content.style.overflow = "visible";
      this.content.classList.add("is-measuring");

      // Force a reflow to ensure the content is rendered
      this.content.offsetHeight;

      // Get the natural height including all padding and margins
      const naturalHeight = this.content.getBoundingClientRect().height;

      // Reset to closed state for animation
      this.content.style.display = "";
      this.content.style.visibility = "";
      this.content.style.height = "0px";
      this.content.style.overflow = "hidden";
      this.content.classList.remove("is-measuring");
      this.content.classList.add("is-opening");

      // Force reflow
      this.content.offsetHeight;

      // Start opening animation
      requestAnimationFrame(() => {
        this.content.style.height = naturalHeight + "px";
      });

      // Clean up after animation
      setTimeout(() => {
        if (this.isOpen) {
          this.content.style.height = "auto";
          this.content.style.overflow = "visible";
          this.content.classList.remove("is-opening");
          this.content.classList.add("is-open");
        }
      }, 400);
    } else {
      // No animation, just open
      this.content.style.height = "auto";
      this.content.style.overflow = "visible";
      this.content.classList.add("is-open");
    }

    this.isOpen = true;
    this.setAttribute("open", "");

    // Dispatch custom event on parent
    const parent = this.closest("accordion-component");
    if (parent) {
      parent.dispatchEvent(
        new CustomEvent("accordion:open", {
          detail: { item: this },
        }),
      );
    }
  }

  close() {
    if (!this.isOpen) return;

    // Update ARIA
    this.header.setAttribute("aria-expanded", "false");

    // Get current height for animation
    const currentHeight = this.content.getBoundingClientRect().height;
    this.content.style.height = currentHeight + "px";
    this.content.style.overflow = "hidden";
    this.content.classList.remove("is-open");
    this.content.classList.add("is-closing");

    // Force reflow
    this.content.offsetHeight;

    // Start closing animation
    requestAnimationFrame(() => {
      this.content.style.height = "0px";
    });

    // Clean up after animation
    setTimeout(() => {
      if (!this.isOpen) {
        this.content.style.height = "";
        this.content.style.overflow = "";
        this.content.classList.remove("is-closing");
      }
    }, 400);

    this.isOpen = false;
    this.removeAttribute("open");

    // Dispatch custom event on parent
    const parent = this.closest("accordion-component");
    if (parent) {
      parent.dispatchEvent(
        new CustomEvent("accordion:close", {
          detail: { item: this },
        }),
      );
    }
  }

  focusNext() {
    const parent = this.closest("accordion-component");
    if (parent) {
      parent.focusNextItem(this);
    }
  }

  focusPrevious() {
    const parent = this.closest("accordion-component");
    if (parent) {
      parent.focusPreviousItem(this);
    }
  }

  focusFirst() {
    const parent = this.closest("accordion-component");
    if (parent) {
      parent.focusFirstItem();
    }
  }

  focusLast() {
    const parent = this.closest("accordion-component");
    if (parent) {
      parent.focusLastItem();
    }
  }
}

// Custom Element: AccordionComponent
class AccordionComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // No specific initialization needed
  }

  get items() {
    return Array.from(this.querySelectorAll("accordion-item"));
  }

  closeAllItems() {
    this.items.forEach((item) => item.close());
  }

  openAllItems() {
    this.items.forEach((item) => item.open());
  }

  focusNextItem(currentItem) {
    const items = this.items;
    const currentIndex = items.indexOf(currentItem);
    const nextIndex = (currentIndex + 1) % items.length;
    if (items[nextIndex]) {
      items[nextIndex].header.focus();
    }
  }

  focusPreviousItem(currentItem) {
    const items = this.items;
    const currentIndex = items.indexOf(currentItem);
    const previousIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
    if (items[previousIndex]) {
      items[previousIndex].header.focus();
    }
  }

  focusFirstItem() {
    const items = this.items;
    if (items.length > 0) {
      items[0].header.focus();
    }
  }

  focusLastItem() {
    const items = this.items;
    if (items.length > 0) {
      items[items.length - 1].header.focus();
    }
  }
}

// Custom Element: AccordionHeader
class AccordionHeader extends HTMLElement {
  connectedCallback() {
    // Just a semantic container, no special behavior needed
  }
}

// Custom Element: AccordionContent
class AccordionContent extends HTMLElement {
  connectedCallback() {
    // Just a semantic container, no special behavior needed
  }
}

// Register custom elements
if (!customElements.get("accordion-component")) {
  customElements.define("accordion-component", AccordionComponent);
}
if (!customElements.get("accordion-item")) {
  customElements.define("accordion-item", AccordionItem);
}
if (!customElements.get("accordion-header")) {
  customElements.define("accordion-header", AccordionHeader);
}
if (!customElements.get("accordion-content")) {
  customElements.define("accordion-content", AccordionContent);
}

// Export for external access
window.AccordionComponent = AccordionComponent;
window.AccordionItem = AccordionItem;
